import { useSyncExternalStore } from "react";

/**
 * The agent's confirmation of a ticket's facts (#820), kept in this browser.
 *
 * Confirming is a habit of the person at the screen, not a fact about the ticket, so it
 * lives here and not in the database: it is per browser, it never reaches another agent
 * or another app (a fact changed in Neptune does not ask anyone to confirm anything in
 * Venus), and clearing site data starts it over. Storage can throw (private windows,
 * blocked site data); then confirmations last for the session only.
 *
 * An entry says what the person last did with one scope, a customer request or a
 * message's general notes: `confirmed: true` is "looked at and confirmed", `false` is
 * "changed since" (their own edit, or a confirmation they took back). No entry means
 * "never looked at", which reads as unconfirmed: a new ticket starts that way.
 */

const STORAGE_KEY = "lytra.fact-confirmations.v1";
/** Oldest entries go first, so the store cannot grow without bound over years of tickets. */
export const MAX_CONFIRMATION_ENTRIES = 500;

export interface ConfirmationEntry {
  confirmed: boolean;
  /** Epoch ms of the last change; orders entries for pruning. */
  at: number;
}
export type ConfirmationEntries = Readonly<Record<string, ConfirmationEntry>>;

/** The scope of one customer request's facts. */
export const requestScope = (customerRequestId: string) => `r:${customerRequestId}`;
/** The scope of a message's general notes, the facts tied to no request. */
export const generalScope = (messageId: string) => `g:${messageId}`;

// Shared across components by design: one snapshot, read by every button on the page, so that
// confirming in one card re-renders the others. Replaced, never mutated, so React sees the change.
let snapshot: ConfirmationEntries | null = null;
const listeners = new Set<() => void>();

function load(): ConfirmationEntries {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const entries: Record<string, ConfirmationEntry> = {};
    for (const [scope, value] of Object.entries(parsed)) {
      const entry = value as Partial<ConfirmationEntry> | null;
      if (entry && typeof entry.confirmed === "boolean" && typeof entry.at === "number") {
        entries[scope] = { confirmed: entry.confirmed, at: entry.at };
      }
    }
    return entries;
  } catch {
    return {};
  }
}

function read(): ConfirmationEntries {
  snapshot ??= load();
  return snapshot;
}

function write(next: ConfirmationEntries): void {
  snapshot = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Session-only: the snapshot above still serves this page.
  }
  listeners.forEach((listener) => listener());
}

function prune(entries: Record<string, ConfirmationEntry>): Record<string, ConfirmationEntry> {
  const scopes = Object.keys(entries);
  if (scopes.length <= MAX_CONFIRMATION_ENTRIES) return entries;
  const keep = scopes
    .sort((a, b) => entries[b].at - entries[a].at)
    .slice(0, MAX_CONFIRMATION_ENTRIES);
  return Object.fromEntries(keep.map((scope) => [scope, entries[scope]]));
}

function set(scope: string, confirmed: boolean): void {
  const current = read();
  if (current[scope]?.confirmed === confirmed) return;
  write(prune({ ...current, [scope]: { confirmed, at: Date.now() } }));
}

/** The person confirmed this scope. */
export const confirmScope = (scope: string): void => set(scope, true);
/** The scope changed after, or without, a confirmation, or the person took theirs back. */
export const markScopeChanged = (scope: string): void => set(scope, false);

/** Forget everything, for tests; storage is the caller's to clear. */
export function resetConfirmationsForTests(): void {
  snapshot = null;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Every confirmation entry, live: re-renders when any scope changes, in this tab or by `storage`. */
export function useFactConfirmations(): ConfirmationEntries {
  return useSyncExternalStore(subscribe, read, () => ({}));
}

// Another tab of the same browser confirmed something: pick it up.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    snapshot = null;
    listeners.forEach((listener) => listener());
  });
}
